import React from 'react';
import { Link2, Check, X } from 'lucide-react';
import { moduleApi } from '../../../lib/api';
import { Field, Textarea } from '../../ui/Field';
import { pillPrimary, pillGhost, pillDestructive, PILL_DESTRUCTIVE_STYLE } from '../../ui/pills';
import { Carte } from './DossierCarte';
import type { Dossier } from '../SuiviPrivatisationLayout';

/** L'état d'un rendez-vous, dans les mots du destinataire : « confirmé » se lit d'un coup d'œil, pas
 *  « confirme ». Le libellé de l'attente dit QUI l'on attend — c'est l'établissement qui valide. */
const LIBELLE_RDV: Record<string, string> = {
    en_attente: 'En attente de votre confirmation',
    confirme: 'Rendez-vous confirmé',
    refuse: 'Rendez-vous refusé',
    annule: 'Rendez-vous annulé',
};
const TON_RDV: Record<string, string> = {
    en_attente: 'bg-state-warn-soft text-state-warn border-state-warn/30',
    confirme: 'bg-state-ok-soft text-state-ok border-state-ok/30',
    refuse: 'bg-state-danger-soft text-state-danger border-state-danger/30',
    annule: 'bg-surface-soft text-content-muted border-border',
};

const fmtDate = (d?: string | null) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return '—'; }
};

/**
 * Le rendez-vous appartient à RDV Pro : on montre ce qu'il en dit, on ne le recopie pas ici, sinon les
 * deux divergeraient au premier report. Accepter/refuser directement depuis le dossier — Emeni ne voulait
 * plus « jongler avec RDV Pro » pour ce geste (2026-08-31). Aucun mécanisme nouveau : mêmes routes, même
 * mail au contact que RDV Pro.
 */
export function RendezVousCard({ dossier, rafraichir }: { dossier: Dossier; rafraichir: () => void }) {
    const rdv = dossier.rendez_vous;
    const [showRefuseForm, setShowRefuseForm] = React.useState(false);
    const [refuseReason, setRefuseReason] = React.useState('');
    const [traitementEnCours, setTraitementEnCours] = React.useState(false);
    const [erreurRdv, setErreurRdv] = React.useState('');

    const accepterRdv = async () => {
        if (!rdv) return;
        setTraitementEnCours(true);
        setErreurRdv('');
        try {
            await moduleApi.acceptProRdvAppointment(rdv.id);
            rafraichir();
        } catch (e: any) {
            setErreurRdv(e?.message || "Le rendez-vous n'a pas pu être accepté.");
        } finally {
            setTraitementEnCours(false);
        }
    };

    const refuserRdv = async () => {
        if (!rdv) return;
        if (!refuseReason.trim()) { setErreurRdv("Merci d'indiquer un motif."); return; }
        setTraitementEnCours(true);
        setErreurRdv('');
        try {
            await moduleApi.refuseProRdvAppointment(rdv.id, refuseReason.trim());
            setShowRefuseForm(false);
            setRefuseReason('');
            rafraichir();
        } catch (e: any) {
            setErreurRdv(e?.message || "Le rendez-vous n'a pas pu être refusé.");
        } finally {
            setTraitementEnCours(false);
        }
    };

    return (
        <Carte titre="Rendez-vous">
            {rdv ? (
                <div className="space-y-2">
                    <p className="text-[12.5px] font-semibold text-content">
                        {rdv.subject || 'Rendez-vous'} — {fmtDate(rdv.appointment_date)} à {rdv.start_time}
                    </p>
                    {/* L'état du rendez-vous manquait : le suivi affichait la date sans jamais dire si
                        l'établissement l'avait confirmé. Signalé par Emeni (2026-08-30). */}
                    <span className={`inline-flex items-center h-[22px] px-[9px] rounded-full border text-[10.5px] font-bold whitespace-nowrap ${TON_RDV[rdv.status] || TON_RDV.en_attente}`}>
                        {LIBELLE_RDV[rdv.status] || rdv.status}
                    </span>

                    {rdv.status === 'en_attente' && (
                        <div className="pt-1 space-y-2">
                            {erreurRdv && <p className="text-[11.5px] text-state-danger font-semibold">{erreurRdv}</p>}
                            {!showRefuseForm ? (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <button type="button" className={pillPrimary(32)} onClick={accepterRdv} disabled={traitementEnCours}>
                                        <Check size={13} /> Accepter le rendez-vous
                                    </button>
                                    <button type="button" className={pillGhost(32)} onClick={() => setShowRefuseForm(true)} disabled={traitementEnCours}>
                                        <X size={13} /> Refuser
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Field label="Motif du refus">
                                        {({ id }) => (
                                            <Textarea id={id} value={refuseReason} onChange={(e) => setRefuseReason(e.target.value)} rows={2}
                                                placeholder="Ex : créneau finalement indisponible" />
                                        )}
                                    </Field>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <button type="button" className={pillDestructive(32)} style={PILL_DESTRUCTIVE_STYLE} onClick={refuserRdv} disabled={traitementEnCours}>
                                            Confirmer le refus
                                        </button>
                                        <button type="button" className={pillGhost(32)}
                                            onClick={() => { setShowRefuseForm(false); setRefuseReason(''); setErreurRdv(''); }}
                                            disabled={traitementEnCours}>
                                            Annuler
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <p className="text-[12px] text-content-muted flex items-center gap-1.5">
                    <Link2 size={12} /> Aucun rendez-vous rattaché — à prendre depuis RDV Pro.
                </p>
            )}
        </Carte>
    );
}
